/**
 * An offering's answered questions as summary rows ("Here's what IT will
 * get", 7 Oct), for the service request form and the chat's request card.
 * Unanswered questions are left out; each row's Edit goes back to its field.
 */

import { type FormReviewRow, useDateFormatter } from "@/components/common";
import { useT } from "@/i18n";

import type { OfferingParameter } from "../api/contract";
import { displayText } from "../lib/translatable";
import { answerFor, type AnswerMap, answerSummary } from "./offering-parameter-fields";

export function useOfferingReviewRows() {
  const { t } = useT("common");
  const { formatDate, formatDateTime } = useDateFormatter();

  return (
    parameters: readonly OfferingParameter[],
    answers: AnswerMap,
    employeeNames: Record<string, string>,
    onEdit: (parameterId: string) => void,
  ): FormReviewRow[] =>
    parameters.flatMap((parameter) => {
      const answer = answerFor(answers, parameter.parameter_id);
      const raw = (answer.value ?? "").trim();
      let value: string;
      switch (parameter.type) {
        case "ATTACHMENT": {
          const count = (answer.attachmentIds ?? []).length;
          value = count === 0 ? "" : t("formCheck.review.files", { count });
          break;
        }
        case "BOOLEAN":
          value =
            raw === "true"
              ? t("formCheck.review.yes")
              : raw === "false"
                ? t("formCheck.review.no")
                : "";
          break;
        case "DATE":
          // Noon, so the calendar day survives any time zone.
          value = raw === "" ? "" : formatDate(new Date(`${raw.slice(0, 10)}T12:00:00`));
          break;
        case "DATETIME":
          value = raw === "" ? "" : formatDateTime(raw);
          break;
        default:
          value = answerSummary(parameter, answer, employeeNames).trim();
      }
      if (value === "") {
        return [];
      }
      return [
        {
          key: parameter.parameter_id,
          label: displayText(parameter.name),
          value,
          onEdit: () => onEdit(parameter.parameter_id),
        },
      ];
    });
}
